import { Button, Textarea, TextInput } from '@mantine/core';
import { type InferSelectModel } from 'drizzle-orm';
import { type models } from 'drizzle/schema';
import {
  type FC,
  type FormEventHandler,
  type ReactNode,
  useEffect,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import slugify from 'slugify';

const FieldContainer: FC<{ readonly children: ReactNode }> = ({ children }) => {
  return (
    <div className="border-l-[3px] border-l-slate-300 has-focus:border-l-sky-500 pl-2">
      {children}
    </div>
  );
};

export type FormFields = {
  attributes: Record<string, string>;
  slug?: string;
  title: string;
};

export const Form = ({
  modelData,
  submitHandler,
  title,
}: {
  readonly modelData: InferSelectModel<typeof models>;
  readonly submitHandler: FormEventHandler;
  readonly title: string;
}) => {
  const { control, formState, register, setValue } =
    useFormContext<FormFields>();

  const titleValue = useWatch({ control, name: 'title' });
  useEffect(() => {
    if (formState.dirtyFields.title) {
      setValue(
        'slug',
        slugify(titleValue, { remove: /[^a-z0-9-\s]/iu }).toLowerCase(),
      );
    }
  }, [formState.dirtyFields.title, formState.isDirty, setValue, titleValue]);

  return (
    <form
      className="p-5 flex flex-col gap-3"
      onSubmit={submitHandler}
    >
      <div className="flex flex-row">
        <div className="grow">{title}</div>
        <Button type="submit">Save</Button>
      </div>
      <FieldContainer>
        <TextInput
          error={formState.errors.title?.message?.toString()}
          label="Title (required)"
          {...register('title')}
        />
      </FieldContainer>
      {modelData.isTitleSlugEnabled && (
        <FieldContainer>
          <TextInput
            error={formState.errors.slug?.message?.toString()}
            label="Slug"
            {...register('slug')}
          />
        </FieldContainer>
      )}

      {modelData?.schema.fields.map((field) => {
        const label = `${field.name}${field.required ? ' (required)' : ''}`;
        const attrName = `attributes.${field.id}` as const;
        const error =
          formState.errors.attributes?.[field.id]?.message?.toString();
        let content;
        if (field.type === 'text') {
          content = (
            <TextInput
              label={label}
              {...register(attrName)}
              error={error}
            />
          );
        }

        if (field.type === 'rich') {
          content = (
            <Textarea
              label={label}
              {...register(attrName)}
              error={error}
            />
          );
        }

        return <FieldContainer key={field.id}>{content}</FieldContainer>;
      })}
    </form>
  );
};
